import { html, css, nothing, LitElement } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import sharedStyles from '../../app/styles.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { distributeComposition, rollbackReplace } from '../../store/comp/comp.js';
import { stopCatalogItemReplaceStatus } from '../../store/catalog/catalog.js';
import { navigateBack } from '../../store/route/route.js';
import { translate } from '../../helpers/translate.js';
import moment from 'moment/dist/moment.js';
import './sdb-distribution-select.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-overlay-element.js';
import '../shared/sdb-overlay-layout.js';
import '../shared/sdb-chipbar-extended.js';

@customElement('sdb-composition')
class SdbComposition extends connect(store)(LitElement) {

    @state()
    accessor info;

    @state()
    accessor replaceStatus;

    @state()
    accessor portals;

    @state()
    accessor filteredPortals;

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor isLoading = false;

    @state()
    accessor isDistributing = false;

    @state()
    accessor error;

    @state()
    accessor distributeArgs = {};

    @state()
    accessor distributeValid = false;

    @state()
    accessor replaceArgs = {};

    @state()
    accessor replaceValid = false;

    @query('#distribute_dialog')
    accessor distributeDialog;

    @query('#replace_dialog')
    accessor replaceDialog;

    @query('#rollback_dialog')
    accessor rollbackDialog;

    get replaceEnabled() {
        return !!this.userSettings?.featureFlags?.includes('replaceCatalogItems');
    }

    get replacedOnPortals() {
        return this.info.portals.filter(p =>
            p.replacesTargetIds?.includes(this.info.log?.currentReplacedTargetId));
    }

    get numberOfPortalsDone() {
        return this.replaceStatus?.statuses.filter(p => p.overallStatus === 'done').length || 0;
    }

    get numberOfPortals() {
        return this.replaceStatus?.statuses.length || 0;
    }

    get replaceRrogress() {
        if (!this.replaceStatus || this.numberOfPortals === 0)
            return 0;
        return Math.floor(100 * this.numberOfPortalsDone / this.numberOfPortals);
    }

    stateChanged(state) {
        this.info = state.comp?.current,
        this.replaceStatus = state.catalog?.replaceStatus;
        this.isLoading = state.comp?.status === 'loading' && !state.comp?.current;
        this.isDistributing = state.comp?.status === 'distributing';
        this.error = state.comp?.error;
        this.user = state.oidc.user;
        this.userSettings = state.env.userSettings;
    };

    static styles = [
        sharedStyles,
        css`
            [slot=content] {
                width: 100%;
                padding: 0 20px;
                max-width: 1400px;
            }

            sdb-content-list {
                padding: 0 !important;
                margin-left: -20px;
            }

            .scroller {
                max-height: 300px;
                overflow-x: auto;
            }

            sdb-content-chipbar, .empty-chipbar {
                margin-top: 10px;
            }

            .empty-chipbar {
                font-weight: 300;
                color: var(--text-primary);
            }

            .buttons {
                width: 100%;
                display: flex;
                justify-content: flex-end;
            }

            div[hidden] {
                display: none;
            }

            paper-input[readonly] { /* somehow this icw the custom-style works */
                --paper-input-container-underline_-_display: none;
                --paper-input-container_-_background-color: #fff;
            }
        `,
    ]

    render() {
        if (this.isLoading || !this.info) {
            return nothing;
        }

        return html`
            <sdb-overlay-layout
                headline=${this.info.composition.title}
                @back=${() => { store.dispatch(navigateBack('compositions')); }}>

                <custom-style>
                    <style>
                        paper-input[readonly]] {
                            --paper-input-container-underline: { display: none; height: 0;};
                        }
                    </style>
                </custom-style>

                <div slot="buttons">
                </div>

                <div slot="content">

                    <vaadin-progress-bar
                        indeterminate
                        ?hidden=${!this.isDistributing}>
                    </vaadin-progress-bar>

                    <sdb-layout-50-50>
                        <div slot="left">
                            <sdb-content-collapsablecard
                                id="distribution_card"
                                headline=${translate('headerPortals')}
                                icon="fal fa-globe"
                                headlineSize="16px"
                                iconSize="32"
                                opened>
                                ${this.info.portals.length
                                    ? html`
                                        <sdb-chipbar-extended
                                            .chips=${this.info.portals.map(p => p.displayName)}
                                            collapsible>
                                        </sdb-chipbar-extended>
                                    ` : html`
                                        <div class="empty-chipbar">${translate('noPortals')}</div>
                                    `
                                }

                                <div class="buttons">
                                    <sdb-content-button
                                        id="distribute_button"
                                        label=${translate('distribute')}
                                        secondary
                                        ?disabled=${this.isDistributing}
                                        @click=${() => { this.distributeDialog.open(); }}>
                                    </sdb-content-button>
                                </div>
                            </sdb-content-collapsablecard>
                            <div><br><br></div>
                            <sdb-content-collapsablecard
                                id="replace_card"
                                headline=${translate('headerReplace')}
                                icon="fal fa-arrows-retweet"
                                headlineSize="16px"
                                iconSize="32"
                                ?hidden=${!this.info.log?.currentReplacedTargetId && !this.replaceEnabled}
                                opened>
                                ${this.info.log?.currentReplacedTargetId
                                    ? html`
                                        <paper-input
                                            readonly
                                            label=${translate('replacedElearning')}
                                            value=${this.info.log.currentReplacedName}>
                                        </paper-input>
                                    ` : html`<div>${translate('noReplacements')}</div>`
                                }

                                ${this.replaceStatus?.overallStatus === 'inProgress' ? html`
                                        <sdb-content-progressbar
                                            progress=${this.replaceRrogress}
                                            value=${this.numberOfPortalsDone}
                                            totalvalue=${this.numberOfPortals}
                                            showPercentage>
                                        </sdb-content-progressbar>
                                        <div>${translate('replaceInProgress')}</div>
                                    ` : html`
                                        <sdb-chipbar-extended
                                            .chips=${this.replacedOnPortals.map(p => p.displayName)}
                                            collapsible>
                                        </sdb-chipbar-extended>
                                    `
                                }

                                <div class="buttons"
                                    ?hidden=${!this.replaceEnabled}>
                                    <sdb-content-button
                                        id="rollback_button"
                                        label=${translate('rollback')}
                                        ?hidden=${!this.info.log?.currentReplacedTargetId || true}
                                        ?disabled=${this.isDistributing || this.replaceStatus?.overallStatus === 'inProgress'}
                                        secondary
                                        @click=${() => { this.rollbackDialog.open(); }}>
                                    </sdb-content-button>
                                    <sdb-content-button
                                        id="replace_button"
                                        label=${translate('replace')}
                                        ?disabled=${this.isDistributing || this.replaceStatus?.overallStatus === 'inProgress'}
                                        secondary
                                        @click=${() => { this.replaceDialog.open(); }}>
                                    </sdb-content-button>
                                </div>

                            </sdb-content-collapsablecard>

                        </div>

                        <div slot="right">
                            <sdb-content-collapsablecard
                                id="publications_card"
                                headline=${translate('headerPublications')}
                                icon="fal fa-bullhorn"
                                headlineSize="16px"
                                iconSize="32"
                                opened>
                                <sdb-content-list class="scroller">
                                    ${map(this.info.log?.publishRecords || [], (record) => html`
                                        <sdb-content-listitem
                                            headline=${moment(record.published.timestamp).format('lll')}
                                            subtitle=${record.published.name}
                                            noPadding
                                            noHover>
                                        </sdb-content-listitem>
                                    `)}
                                </sdb-content-list>
                            </sdb-content-collapsablecard>
                            <div><br><br></div>

                            <sdb-content-collapsablecard
                                id="distributions_card"
                                headline=${translate('headerDistributions')}
                                icon="fal fa-book-atlas"
                                headlineSize="16px"
                                iconSize="32"
                                ?hidden=${!this.info.log?.distributionRecords}
                                opened>
                                <sdb-content-list class="scroller">
                                    ${map(this.info.log?.distributionRecords || [], (record) => html`
                                        <sdb-content-listitem
                                            headline=${moment(record.distributed.timestamp).format('lll')}
                                            subtitle=${record.distributed.name}
                                            noPadding
                                            noHover>
                                        </sdb-content-listitem>
                                    `)}
                                </sdb-content-list>
                            </sdb-content-collapsablecard>
                        </div>
                    </sdb-layout-50-50>
                </div>

            </sdb-overlay-layout>

            <sdb-dialog
                type="form"
                id="distribute_dialog"
                heading=${translate(`distributeHeading`)}
                confirmCaption=${translate('distribute')}
                ?confirmDisabled=${!this.distributeValid}
                .bodyRenderer=${() => html`
                    <sdb-distribution-select
                        @selected=${(e) => {
                            this.distributeValid = e.detail.valid;
                            this.distributeArgs = e.detail.args;
                        }}>
                    </sdb-distribution-select>
                `}
                @confirm=${() => {
                    store.dispatch(distributeComposition({
                        composition: this.info.composition,
                        portalIds: this.distributeArgs.portalIds,
                        flags: this.distributeArgs.flags,
                    }));
                }}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="replace_dialog"
                heading=${translate(`replaceHeading`)}
                confirmCaption=${translate('replace')}
                ?confirmDisabled=${!this.replaceValid}
                .bodyRenderer=${() => html`
                    <sdb-distribution-select
                        replace
                        @selected=${(e) => {
                            this.replaceValid = e.detail.valid;
                            this.replaceArgs = e.detail.args;
                        }}>
                    </sdb-distribution-select>
                `}
                @confirm=${() => {
                    store.dispatch(distributeComposition({
                        composition: this.info.composition,
                        portalIds: this.replaceArgs.portalIds,
                        flags: this.replaceArgs.flags,
                        replaceTargetId: this.replaceArgs.replaceTargetId,
                        replaceName: this.replaceArgs.replaceName
                    }));
                }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="rollback_dialog"
                heading=${translate('rollbackHeading')}
                body=${translate('rollbackBody')}
                confirmCaption=${translate('rollback')}
                @confirm=${(e) => {
                    store.dispatch(rollbackReplace({
                        composition: this.info.composition,
                        portalIds: this.info.portals.map((p) => p.portalIdentifier),
                        replaceTargetId: this.info.log.currentReplacedTargetId,
                    }));
                }}>
            </sdb-dialog>
        `;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        store.dispatch(stopCatalogItemReplaceStatus());
    }
}
